import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import ReelButton from '@/components/generals/reel-button'
import { NAV_LINKS, siteConfig } from '@/constants/site'

interface SiteFooterProps {
    variant?: 'full' | 'minimal'
}

const SiteFooter: FC<SiteFooterProps> = ({ variant = 'full' }): ReactNode => {
    return (
        <footer id='site-footer' className='relative overflow-hidden border-t border-white/[0.06] bg-ink'>
            <div aria-hidden className='film-grain pointer-events-none absolute inset-0 opacity-[0.035]' />

            {variant === 'full' && (
                <section id='lets-connect' aria-labelledby='lets-connect-title' className='relative px-[var(--site-gutter)] pb-16 pt-24 md:pb-20 md:pt-36'>
                    <p className='text-[10px] font-medium uppercase tracking-[0.4em] text-white/50'>
                        Something in mind?
                    </p>

                    <h2
                        id='lets-connect-title'
                        className='mt-6 font-heading text-[clamp(3.4rem,12vw,12rem)] font-semibold uppercase leading-[0.86] tracking-[-0.025em] text-bone'
                    >
                        Let&apos;s
                        <span className='block font-normal italic normal-case tracking-[-0.03em] text-ivory'>connect</span>
                    </h2>

                    <div className='mt-12 flex flex-col gap-8 md:mt-16 md:flex-row md:items-end md:justify-between'>
                        <a
                            href={`mailto:${siteConfig.email}`}
                            className='group w-fit font-heading text-[clamp(1.35rem,3.2vw,2.6rem)] italic leading-tight text-white/85 transition-colors hover:text-white'
                        >
                            {siteConfig.email}
                            <span aria-hidden className='mt-1 block h-px w-full origin-left scale-x-[0.18] bg-white/50 transition-transform duration-500 ease-out group-hover:scale-x-100' />
                        </a>

                        <ReelButton href='/contact'>Start a project</ReelButton>
                    </div>
                </section>
            )}

            <div className='relative flex flex-col gap-6 border-t border-white/[0.06] px-[var(--site-gutter)] py-8 text-[10.5px] font-medium uppercase tracking-[0.3em] text-white/45 lg:flex-row lg:items-center lg:justify-between'>
                <p>
                    &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}
                    <span aria-hidden className='mx-3 text-white/20'>/</span>
                    {siteConfig.location}
                </p>

                <nav aria-label='Footer' className='flex flex-wrap items-center gap-x-8 gap-y-3'>
                    {NAV_LINKS.map(link => (
                        <Link key={link.href} href={link.href} className='transition-colors hover:text-white'>
                            {link.label}
                        </Link>
                    ))}

                    <a href={siteConfig.links.instagram} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1.5 transition-colors hover:text-white'>
                        Instagram
                        <ArrowUpRight className='size-3.5' strokeWidth={1.5} />
                    </a>

                    <a href={siteConfig.links.vimeo} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1.5 transition-colors hover:text-white'>
                        Vimeo
                        <ArrowUpRight className='size-3.5' strokeWidth={1.5} />
                    </a>
                </nav>
            </div>
        </footer>
    )
}

export default SiteFooter
